import { normalizeCapabilitiesList } from './capabilityApiNormalize';
import { FRAMEWORK_STUDIO_LABELS } from './frameworkStudioLabels';
import { resolveFrameworkSourceTypeName } from './sourceTypeCatalog';
import { QUESTION_STATE, type SubCapabilityQuestionDraft } from './subCapabilityQuestionUtils';

export const MASTER_LIST_FILTER_ANY = '__any__';

/** Fixed edge columns — sticky + colgroup; data columns use % for the remainder. */
export const MASTER_LIST_SELECT_COL_WIDTH = '2.75rem';
export const MASTER_LIST_ACTIONS_COL_WIDTH = '3.25rem';

export const MASTER_LIST_VIEW_MODES = [
  { id: 'capability', label: 'Capabilities' },
  { id: 'subCapability', label: 'Sub-capabilities' },
  { id: 'question', label: 'Questions' },
] as const;

export type MasterListViewMode = (typeof MASTER_LIST_VIEW_MODES)[number]['id'];

/** Shown on question rows that belong to a capability with no children. */
export const DIRECT_QUESTIONS_LABEL = 'No sub-capabilities, direct questions';

export const QUESTION_STATE_LABEL = {
  [QUESTION_STATE.current]: 'Current State',
  [QUESTION_STATE.future]: 'Future State',
} as const;

export type MasterListRow = {
  rowKey: string;
  viewMode: MasterListViewMode;
  capabilityId: number;
  capabilityName: string;
  capabilityDescription: string;
  capabilityIsActive: boolean;
  frameworkId: number | null;
  sourceFrameworkName: string;
  frameworkReferenceCompany: string;
  frameworkType: string;
  frameworkDescription: string;
  businessTypeId: number | null;
  businessTypeName: string;
  subId: number;
  subCapabilityName: string;
  subCapabilityDescription: string;
  subIsActive: boolean;
  subCapabilityCount: number;
  subCapabilitySummary: string;
  questionCount: number;
  questionId: number;
  questionText: string;
  questionState: string;
  questionDisplayOrder: number;
  questionIsActive: boolean;
  currentStateQuestions: SubCapabilityQuestionDraft[];
  futureStateQuestions: SubCapabilityQuestionDraft[];
};

export type MasterListColumnKey = keyof Pick<
  MasterListRow,
  | 'questionText'
  | 'questionState'
  | 'questionIsActive'
  | 'subCapabilityName'
  | 'subCapabilityDescription'
  | 'subIsActive'
  | 'capabilityName'
  | 'capabilityDescription'
  | 'capabilityIsActive'
  | 'sourceFrameworkName'
  | 'frameworkReferenceCompany'
  | 'frameworkType'
  | 'frameworkDescription'
  | 'businessTypeName'
  | 'subCapabilityCount'
  | 'questionCount'
>;

export type MasterListColumnDef = {
  key: MasterListColumnKey;
  label: string;
  shortLabel: string;
  width: string;
  sticky?: boolean;
  primary?: boolean;
  clampLines?: number;
  filterable?: boolean;
  numeric?: boolean;
};

const col = (
  key: MasterListColumnKey,
  label: string,
  shortLabel: string,
  width: string,
  extras: Partial<Omit<MasterListColumnDef, 'key' | 'label' | 'shortLabel' | 'width'>> = {}
): MasterListColumnDef => ({
  key,
  label,
  shortLabel,
  width,
  clampLines: 2,
  ...extras,
});

export const MASTER_LIST_COLUMNS_BY_VIEW: Record<MasterListViewMode, MasterListColumnDef[]> = {
  capability: [
    col('capabilityName', 'Capability', 'Capability', '17%', { sticky: true, primary: true }),
    col('capabilityIsActive', 'Status', 'Status', '5.75rem', { clampLines: 1 }),
    col('frameworkType', FRAMEWORK_STUDIO_LABELS.sourceType, 'Source type', '14%', {
      clampLines: 1,
    }),
    col('sourceFrameworkName', 'Source Framework Name', 'Source fw.', '16%'),
    col('frameworkReferenceCompany', 'Reference Company', 'Ref. company', '12%', { clampLines: 1 }),
    col('subCapabilityCount', 'Sub-capabilities', 'Sub-caps', '9%', { clampLines: 1, numeric: true }),
    col('questionCount', 'Questions', 'Questions', '9%', { clampLines: 1, numeric: true }),
    col('businessTypeName', 'User Type', 'User type', '10%', { clampLines: 1 }),
  ],
  subCapability: [
    col('subCapabilityName', 'Sub-Capability', 'Sub-capability', '20%', { sticky: true, primary: true }),
    col('subIsActive', 'Status', 'Status', '8%', { clampLines: 1 }),
    col('sourceFrameworkName', 'Source Framework Name', 'Source fw.', '15%'),
    col('frameworkReferenceCompany', 'Reference Company', 'Ref. company', '11%', { clampLines: 1 }),
    col('capabilityName', 'Capability', 'Capability', '14%'),
    col('businessTypeName', 'User Type', 'User type', '11%', { clampLines: 1 }),
    col('questionCount', 'Questions', 'Questions', '8%', { clampLines: 1, numeric: true }),
  ],
  question: [
    col('questionText', 'Question', 'Question', '24%', { sticky: true, primary: true, filterable: false }),
    col('questionState', 'State', 'State', '8%', { clampLines: 1 }),
    col('questionIsActive', 'Status', 'Status', '7%', { clampLines: 1 }),
    col('sourceFrameworkName', 'Source Framework Name', 'Source fw.', '12%'),
    col('frameworkReferenceCompany', 'Reference Company', 'Ref. company', '10%', { clampLines: 1 }),
    col('capabilityName', 'Capability', 'Capability', '10%'),
    col('businessTypeName', 'User Type', 'User type', '8%', { clampLines: 1 }),
    col('subCapabilityName', 'Sub-Capability', 'Sub-capability', '14%'),
  ],
};

export const MASTER_LIST_PAGE_COPY = {
  title: FRAMEWORK_STUDIO_LABELS.assessmentFrameworks,
  description: FRAMEWORK_STUDIO_LABELS.assessmentFrameworksBuilderDescription,
  empty: 'No capabilities match the current filters.',
  selectAll: 'Select all visible capabilities',
};

/** Capability Catalog — reference-only (no selection); table columns via getCapabilityCatalogColumns(). */
export const CAPABILITY_CATALOG_PAGE_COPY = {
  title: FRAMEWORK_STUDIO_LABELS.capabilityCatalog,
  description:
    'Reference catalog of capabilities with source document, reference company, source type, and assessment metadata.',
  empty: 'No capabilities match the current filters.',
};

export const CAPABILITY_CATALOG_SUB_COPY = {
  title: 'Sub-capabilities',
  description: 'Click a sub-capability to view its assessment questions.',
  empty: 'This capability has no sub-capabilities.',
};

export const MASTER_LIST_VIEW_COPY: Record<
  MasterListViewMode,
  { title: string; description: string; empty: string; selectAll: string }
> = {
  capability: {
    title: MASTER_LIST_PAGE_COPY.title,
    description: MASTER_LIST_PAGE_COPY.description,
    empty: MASTER_LIST_PAGE_COPY.empty,
    selectAll: MASTER_LIST_PAGE_COPY.selectAll,
  },
  subCapability: {
    title: 'Sub-capabilities',
    description: 'Select one or more. Their questions are included automatically.',
    empty: 'This capability has no sub-capabilities.',
    selectAll: 'Select all sub-capabilities',
  },
  question: {
    title: 'Questions',
    description: 'Preview only — questions are included automatically when you create an assessment framework.',
    empty: 'No questions are available here.',
    selectAll: 'Select all questions',
  },
};

export const getMasterListColumns = (viewMode: MasterListViewMode) =>
  MASTER_LIST_COLUMNS_BY_VIEW[viewMode];

export const getMasterListFilterableColumns = (viewMode: MasterListViewMode) =>
  getMasterListColumns(viewMode).filter((column) => column.filterable !== false);

export const emptyMasterListFilters = (
  viewMode: MasterListViewMode
): Record<MasterListColumnKey, string> =>
  Object.fromEntries(
    getMasterListFilterableColumns(viewMode).map(({ key }) => [key, MASTER_LIST_FILTER_ANY])
  ) as Record<MasterListColumnKey, string>;

const displayCell = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Active' : 'Inactive';
  return String(value);
};

export const formatMasterListCell = displayCell;

export const formatMasterListQuestionCount = (count: number): string =>
  Number(count) === 0 ? 'N/A' : String(count);

export const formatMasterListQuestionCountLabel = (count: number): string => {
  if (Number(count) === 0) return 'N/A';
  return `${count} ${count === 1 ? 'question' : 'questions'}`;
};

export const formatMasterListColumnDisplay = (
  key: MasterListColumnKey,
  value: unknown
): string => {
  if (key === 'questionCount') {
    return formatMasterListQuestionCount(Number(value));
  }
  return formatMasterListCell(value);
};

export const STATUS_COLUMN_KEYS: MasterListColumnKey[] = [
  'capabilityIsActive',
  'subIsActive',
  'questionIsActive',
];

export const isMasterListStatusColumn = (key: MasterListColumnKey) =>
  STATUS_COLUMN_KEYS.includes(key);

export const isMasterListStatusActive = (row: MasterListRow, key: MasterListColumnKey) => {
  if (key === 'questionIsActive') return row.questionIsActive;
  if (key === 'subIsActive') return row.subIsActive;
  return row.capabilityIsActive;
};

export type MasterListDetailField = {
  label: string;
  value: string | number | boolean | null | undefined;
  fullWidth?: boolean;
};

export type MasterListDetailGroup = {
  title: string;
  fields: MasterListDetailField[];
};

const formatQuestionList = (questions?: SubCapabilityQuestionDraft[] | null) => {
  const texts = (questions || [])
    .map((question) => String(question?.questionText || '').trim())
    .filter(Boolean);
  if (!texts.length) return undefined;
  return texts.map((text, index) => `${index + 1}. ${text}`).join('\n');
};

const frameworkGroup = (row: MasterListRow): MasterListDetailGroup => ({
  title: 'Framework',
  fields: [
    { label: 'Source Framework Name', value: row.sourceFrameworkName },
    { label: 'Reference Company', value: row.frameworkReferenceCompany },
    { label: FRAMEWORK_STUDIO_LABELS.sourceType, value: row.frameworkType, fullWidth: true },
    { label: 'Description', value: row.frameworkDescription, fullWidth: true },
    { label: 'Framework ID', value: row.frameworkId ?? undefined },
  ],
});

const businessTypeGroup = (row: MasterListRow): MasterListDetailGroup => ({
  title: 'User Type',
  fields: [
    { label: 'Name', value: row.businessTypeName },
    { label: 'User Type ID', value: row.businessTypeId ?? undefined },
  ],
});

const capabilityGroup = (row: MasterListRow): MasterListDetailGroup => ({
  title: 'Capability',
  fields: [
    { label: 'Name', value: row.capabilityName },
    { label: 'Description', value: row.capabilityDescription, fullWidth: true },
    { label: 'Status', value: row.capabilityIsActive },
    { label: 'Capability ID', value: row.capabilityId || undefined },
  ],
});

export const buildMasterListDetailGroups = (row: MasterListRow): MasterListDetailGroup[] => {
  if (row.viewMode === 'capability') {
    return [
      {
        title: 'Capability',
        fields: [
          { label: 'Name', value: row.capabilityName },
          { label: 'Description', value: row.capabilityDescription, fullWidth: true },
          { label: 'Status', value: row.capabilityIsActive },
          { label: 'Sub-capabilities', value: row.subCapabilityCount },
          { label: 'Questions', value: row.questionCount },
          {
            label: 'Sub-capability list',
            value: row.subCapabilityCount > 0 ? row.subCapabilitySummary : DIRECT_QUESTIONS_LABEL,
            fullWidth: true,
          },
          { label: 'Capability ID', value: row.capabilityId || undefined },
        ],
      },
      frameworkGroup(row),
      businessTypeGroup(row),
    ];
  }

  if (row.viewMode === 'subCapability') {
    return [
      {
        title: 'Sub-Capability',
        fields: [
          { label: 'Name', value: row.subCapabilityName },
          { label: 'Description', value: row.subCapabilityDescription, fullWidth: true },
          { label: 'Status', value: row.subIsActive },
          { label: 'Questions', value: row.questionCount },
          { label: 'Current State', value: formatQuestionList(row.currentStateQuestions), fullWidth: true },
          { label: 'Future State', value: formatQuestionList(row.futureStateQuestions), fullWidth: true },
          { label: 'Sub-Capability ID', value: row.subId || undefined },
        ],
      },
      capabilityGroup(row),
      frameworkGroup(row),
      businessTypeGroup(row),
    ];
  }

  return [
    {
      title: 'Question',
      fields: [
        { label: 'Question', value: row.questionText, fullWidth: true },
        { label: 'State', value: row.questionState },
        { label: 'Status', value: row.questionIsActive },
        { label: 'Display Order', value: row.questionDisplayOrder + 1 },
        { label: 'Question ID', value: row.questionId || undefined },
      ],
    },
    {
      title: 'Sub-Capability',
      fields: row.subId
        ? [
            { label: 'Name', value: row.subCapabilityName },
            { label: 'Description', value: row.subCapabilityDescription, fullWidth: true },
            { label: 'Status', value: row.subIsActive },
            { label: 'Sub-Capability ID', value: row.subId || undefined },
          ]
        : [{ label: 'Owner', value: DIRECT_QUESTIONS_LABEL, fullWidth: true }],
    },
    capabilityGroup(row),
    frameworkGroup(row),
    businessTypeGroup(row),
  ];
};

export const getMasterListDetailTitle = (row: MasterListRow) => {
  if (row.viewMode === 'capability') return row.capabilityName || 'Capability details';
  if (row.viewMode === 'subCapability') return row.subCapabilityName || 'Sub-capability details';
  return row.questionText || 'Question details';
};

export const getMasterListDetailSubtitle = (row: MasterListRow) => {
  if (row.viewMode === 'capability') {
    return [row.businessTypeName, row.sourceFrameworkName].filter(Boolean).join(' · ') || 'Master list record';
  }
  if (row.viewMode === 'subCapability') {
    return [row.capabilityName, row.sourceFrameworkName].filter(Boolean).join(' · ') || 'Master list record';
  }
  return (
    [row.questionState, row.subCapabilityName, row.capabilityName].filter(Boolean).join(' · ') ||
    'Master list record'
  );
};

export const getMasterListRowLabel = (row: MasterListRow) => {
  if (row.viewMode === 'capability') return row.capabilityName;
  if (row.viewMode === 'subCapability') return row.subCapabilityName;
  return row.questionText;
};

const questionStateLabel = (stateKind?: string) =>
  stateKind === QUESTION_STATE.future
    ? QUESTION_STATE_LABEL[QUESTION_STATE.future]
    : QUESTION_STATE_LABEL[QUESTION_STATE.current];

const questionStateRank = (label: string) =>
  label === QUESTION_STATE_LABEL[QUESTION_STATE.future] ? 1 : 0;

const collectSubQuestions = (
  sub: {
    currentStateQuestions?: SubCapabilityQuestionDraft[];
    futureStateQuestions?: SubCapabilityQuestionDraft[];
  }
): SubCapabilityQuestionDraft[] => [
  ...(sub.currentStateQuestions || []),
  ...(sub.futureStateQuestions || []),
];

const namedQuestions = (questions: SubCapabilityQuestionDraft[]) =>
  questions.filter((question) => String(question?.questionText || '').trim());

type CatalogCapability = ReturnType<typeof normalizeCapabilitiesList>[number];
type CatalogSub = CatalogCapability['subCapabilities'][number];

const isNamedSub = (sub: CatalogSub) =>
  Boolean(sub?.subId) && String(sub?.subCapabilityName || '').trim().length > 0;

type CatalogContext = {
  capabilityId: number;
  capabilityName: string;
  capabilityDescription: string;
  capabilityIsActive: boolean;
  frameworkId: number | null;
  sourceFrameworkName: string;
  frameworkReferenceCompany: string;
  frameworkType: string;
  frameworkDescription: string;
  businessTypeId: number | null;
  businessTypeName: string;
};

const emptyRow = (viewMode: MasterListViewMode, context: CatalogContext): MasterListRow => ({
  rowKey: '',
  viewMode,
  ...context,
  subId: 0,
  subCapabilityName: '',
  subCapabilityDescription: '',
  subIsActive: true,
  subCapabilityCount: 0,
  subCapabilitySummary: '',
  questionCount: 0,
  questionId: 0,
  questionText: '',
  questionState: '',
  questionDisplayOrder: 0,
  questionIsActive: true,
  currentStateQuestions: [],
  futureStateQuestions: [],
});

const compareText = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/** Empty / missing framework types sort after named types. */
const compareFrameworkType = (a: string, b: string) => {
  const aEmpty = !String(a || '').trim();
  const bEmpty = !String(b || '').trim();
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  return compareText(a, b);
};

export const buildMasterListRows = (
  viewMode: MasterListViewMode,
  capabilities: ReturnType<typeof normalizeCapabilitiesList>,
  frameworkById: Record<number, Record<string, unknown>>,
  businessTypeById: Record<number, { id?: number; name?: string }>,
  sourceTypeById?: Record<number, { name: string }>
): MasterListRow[] => {
  const rows: MasterListRow[] = [];

  for (const cap of capabilities || []) {
    const capabilityId = cap.id || cap.capabilityId || 0;
    if (!capabilityId) continue;

    const frameworkId = cap.frameworkId ?? null;
    const fw = frameworkId != null ? frameworkById[frameworkId] : null;
    const businessTypeId = cap.businessTypeId ?? null;
    const businessType = businessTypeId != null ? businessTypeById[businessTypeId] : null;
    const subs = (cap.subCapabilities || []) as CatalogSub[];
    const namedSubs = subs.filter(isNamedSub);

    const context: CatalogContext = {
      capabilityId,
      capabilityName: cap.capabilityName || '',
      capabilityDescription: cap.capabilityDescription || '',
      capabilityIsActive: cap.isActive !== false,
      frameworkId,
      sourceFrameworkName:
        (fw?.sourceFrameworkName as string) ||
        cap.sourceFrameworkName ||
        cap.frameworkTitle ||
        '',
      frameworkReferenceCompany: (fw?.referenceCompanyName as string) || '',
      frameworkType: resolveFrameworkSourceTypeName(fw, sourceTypeById),
      frameworkDescription: (fw?.description as string) || '',
      businessTypeId,
      businessTypeName: businessType?.name || '',
    };

    if (viewMode === 'capability') {
      const parentQuestions = namedSubs.length
        ? []
        : namedQuestions(collectSubQuestions(cap));
      const questionCount = namedSubs.length
        ? namedSubs.reduce(
            (sum, sub) => sum + namedQuestions(collectSubQuestions(sub)).length,
            0
          )
        : parentQuestions.length;

      rows.push({
        ...emptyRow('capability', context),
        rowKey: `cap-${capabilityId}`,
        subCapabilityCount: namedSubs.length,
        subCapabilitySummary: namedSubs
          .map((sub) => sub.subCapabilityName)
          .filter(Boolean)
          .join(', '),
        questionCount,
      });
      continue;
    }

    if (namedSubs.length === 0) {
      if (viewMode === 'question') {
        const currentStateQuestions = namedQuestions(cap.currentStateQuestions || []);
        const futureStateQuestions = namedQuestions(cap.futureStateQuestions || []);
        const questions = [...currentStateQuestions, ...futureStateQuestions];

        questions.forEach((question, questionIndex) => {
          const questionText = String(question.questionText || '').trim();
          const questionId = Number(question.id) || 0;
          const questionDisplayOrder = Number.isFinite(Number(question.displayOrder))
            ? Number(question.displayOrder)
            : questionIndex;

          rows.push({
            ...emptyRow('question', context),
            rowKey: questionId
              ? `q-${questionId}`
              : `cap-${capabilityId}-${question.stateKind || QUESTION_STATE.current}-${questionDisplayOrder}-${questionIndex}`,
            subCapabilityName: DIRECT_QUESTIONS_LABEL,
            questionCount: 1,
            questionId,
            questionText,
            questionState: questionStateLabel(question.stateKind),
            questionDisplayOrder,
            questionIsActive: question.isActive !== false,
            currentStateQuestions,
            futureStateQuestions,
          });
        });
      }
      continue;
    }

    for (const sub of namedSubs) {
      const currentStateQuestions = namedQuestions(sub.currentStateQuestions || []);
      const futureStateQuestions = namedQuestions(sub.futureStateQuestions || []);
      const questions = [...currentStateQuestions, ...futureStateQuestions];

      if (viewMode === 'subCapability') {
        rows.push({
          ...emptyRow('subCapability', context),
          rowKey: `sub-${sub.subId}`,
          subId: sub.subId,
          subCapabilityName: sub.subCapabilityName || '',
          subCapabilityDescription: sub.subCapabilityDescription || '',
          subIsActive: sub.isActive !== false,
          questionCount: questions.length,
          currentStateQuestions,
          futureStateQuestions,
        });
        continue;
      }

      questions.forEach((question, questionIndex) => {
        const questionText = String(question.questionText || '').trim();
        const questionId = Number(question.id) || 0;
        const questionDisplayOrder = Number.isFinite(Number(question.displayOrder))
          ? Number(question.displayOrder)
          : questionIndex;

        rows.push({
          ...emptyRow('question', context),
          rowKey: questionId
            ? `q-${questionId}`
            : `sub-${sub.subId}-${question.stateKind || QUESTION_STATE.current}-${questionDisplayOrder}-${questionIndex}`,
          subId: sub.subId,
          subCapabilityName: sub.subCapabilityName || '',
          subCapabilityDescription: sub.subCapabilityDescription || '',
          subIsActive: sub.isActive !== false,
          questionCount: 1,
          questionId,
          questionText,
          questionState: questionStateLabel(question.stateKind),
          questionDisplayOrder,
          questionIsActive: question.isActive !== false,
          currentStateQuestions,
          futureStateQuestions,
        });
      });
    }
  }

  return rows.sort((a, b) => {
    if (viewMode === 'capability') {
      const typeCompare = compareFrameworkType(a.frameworkType, b.frameworkType);
      if (typeCompare) return typeCompare;
      const capabilityCompare = compareText(a.capabilityName, b.capabilityName);
      if (capabilityCompare) return capabilityCompare;
      return compareText(a.sourceFrameworkName, b.sourceFrameworkName);
    }

    if (viewMode === 'subCapability') {
      const subCompare = compareText(a.subCapabilityName, b.subCapabilityName);
      if (subCompare) return subCompare;
      const frameworkCompare = compareText(a.sourceFrameworkName, b.sourceFrameworkName);
      if (frameworkCompare) return frameworkCompare;
      return compareText(a.capabilityName, b.capabilityName);
    }

    const questionCompare = compareText(a.questionText, b.questionText);
    if (questionCompare) return questionCompare;
    const frameworkCompare = compareText(a.sourceFrameworkName, b.sourceFrameworkName);
    if (frameworkCompare) return frameworkCompare;
    const capabilityCompare = compareText(a.capabilityName, b.capabilityName);
    if (capabilityCompare) return capabilityCompare;
    const subCompare = compareText(a.subCapabilityName, b.subCapabilityName);
    if (subCompare) return subCompare;
    const stateCompare = questionStateRank(a.questionState) - questionStateRank(b.questionState);
    if (stateCompare) return stateCompare;
    return a.questionDisplayOrder - b.questionDisplayOrder;
  });
};

const rowMatchesFilters = (
  row: MasterListRow,
  filters: Partial<Record<MasterListColumnKey, string>>,
  viewMode: MasterListViewMode,
  exceptKey?: MasterListColumnKey
) =>
  getMasterListFilterableColumns(viewMode).every(({ key }) => {
    if (key === exceptKey) return true;
    const selected = filters[key] || MASTER_LIST_FILTER_ANY;
    if (selected === MASTER_LIST_FILTER_ANY) return true;
    return displayCell(row[key]) === selected;
  });

export const getFilterOptions = (
  rows: MasterListRow[],
  key: MasterListColumnKey,
  filters: Partial<Record<MasterListColumnKey, string>> = {},
  viewMode: MasterListViewMode = 'capability',
  extraValues?: string[]
): string[] => {
  const values = new Set<string>();
  for (const row of rows) {
    if (!rowMatchesFilters(row, filters, viewMode, key)) continue;
    const label = displayCell(row[key]);
    if (label !== '—') values.add(label);
  }
  for (const value of extraValues || []) {
    const trimmed = String(value || '').trim();
    if (trimmed) values.add(trimmed);
  }
  return [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

export const sanitizeMasterListFilters = (
  rows: MasterListRow[],
  filters: Partial<Record<MasterListColumnKey, string>>,
  viewMode: MasterListViewMode,
  filterOptionExtras?: Partial<Record<MasterListColumnKey, string[]>>
): Record<MasterListColumnKey, string> => {
  const next = {
    ...emptyMasterListFilters(viewMode),
    ...filters,
  };
  const keys = getMasterListFilterableColumns(viewMode).map((column) => column.key);
  let changed = true;
  let guard = keys.length + 2;

  while (changed && guard > 0) {
    changed = false;
    guard -= 1;
    for (const key of keys) {
      const selected = next[key] || MASTER_LIST_FILTER_ANY;
      if (selected === MASTER_LIST_FILTER_ANY) continue;
      const options = getFilterOptions(
        rows,
        key,
        next,
        viewMode,
        filterOptionExtras?.[key]
      );
      if (!options.includes(selected)) {
        next[key] = MASTER_LIST_FILTER_ANY;
        changed = true;
      }
    }
  }

  return next;
};

export const applyMasterListFilter = (
  rows: MasterListRow[],
  filters: Partial<Record<MasterListColumnKey, string>>,
  viewMode: MasterListViewMode,
  key: MasterListColumnKey,
  value: string,
  filterOptionExtras?: Partial<Record<MasterListColumnKey, string[]>>
): Record<MasterListColumnKey, string> =>
  sanitizeMasterListFilters(
    rows,
    { ...filters, [key]: value },
    viewMode,
    filterOptionExtras
  );

export const filterMasterListRows = (
  rows: MasterListRow[],
  filters: Partial<Record<MasterListColumnKey, string>>,
  viewMode: MasterListViewMode
): MasterListRow[] => rows.filter((row) => rowMatchesFilters(row, filters, viewMode));

const uniquePositiveIds = (values: Array<number | null | undefined>) =>
  [...new Set(values.map((value) => Number(value) || 0).filter((id) => id > 0))];

/** Drop keys that no longer belong to the current catalog scope. */
export const pruneMasterListKeys = (prev: Set<string>, validKeys: Set<string>) => {
  let changed = false;
  const next = new Set<string>();
  for (const key of prev) {
    if (validKeys.has(key)) next.add(key);
    else changed = true;
  }
  return changed ? next : prev;
};

export const EMPTY_MASTER_LIST_ROWS: MasterListRow[] = [];

export const toggleMasterListKey = (prev: Set<string>, key: string) => {
  const next = new Set(prev);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
};

export const assignMasterListKeys = (prev: Set<string>, keys: string[], selected: boolean) => {
  const next = new Set(prev);
  if (selected) keys.forEach((key) => next.add(key));
  else keys.forEach((key) => next.delete(key));
  return next;
};

const pushRowByNumber = (map: Map<number, MasterListRow[]>, id: number, row: MasterListRow) => {
  const list = map.get(id);
  if (list) list.push(row);
  else map.set(id, [row]);
};

export const groupRowsByCapabilityId = (rows: MasterListRow[]) => {
  const map = new Map<number, MasterListRow[]>();
  for (const row of rows) pushRowByNumber(map, row.capabilityId, row);
  return map;
};

export const groupRowsBySubId = (rows: MasterListRow[]) => {
  const map = new Map<number, MasterListRow[]>();
  for (const row of rows) {
    if (row.subId > 0) pushRowByNumber(map, row.subId, row);
  }
  return map;
};

export const groupDirectQuestionRowsByCapabilityId = (rows: MasterListRow[]) => {
  const map = new Map<number, MasterListRow[]>();
  for (const row of rows) {
    if (!(row.subId > 0)) pushRowByNumber(map, row.capabilityId, row);
  }
  return map;
};

export const orderQuestionRowsForPicker = (rows: MasterListRow[]) =>
  [...rows].sort((a, b) => {
    const stateCompare = questionStateRank(a.questionState) - questionStateRank(b.questionState);
    if (stateCompare) return stateCompare;
    const orderCompare = a.questionDisplayOrder - b.questionDisplayOrder;
    if (orderCompare) return orderCompare;
    return compareText(a.questionText, b.questionText);
  });

export const splitQuestionRowsByState = (rows: MasterListRow[]) => {
  const ordered = orderQuestionRowsForPicker(rows);
  return {
    current: ordered.filter(
      (row) => row.questionState === QUESTION_STATE_LABEL[QUESTION_STATE.current]
    ),
    future: ordered.filter(
      (row) => row.questionState === QUESTION_STATE_LABEL[QUESTION_STATE.future]
    ),
  };
};

export const isDirectQuestionRow = (row: MasterListRow) =>
  row.viewMode === 'question' && !(row.subId > 0);

export const getQuestionOwnerKey = (row: MasterListRow) =>
  row.subId > 0 ? `sub-${row.subId}` : `cap-${row.capabilityId}`;

export type MasterListQuestionOwner = {
  ownerKey: string;
  ownerName: string;
  capabilityName: string;
  capabilityId: number;
  isDirect: boolean;
};

/** Owners that must have ≥1 current and ≥1 future question. */
export const getRequiredQuestionOwners = (
  selectedCapabilityRows: MasterListRow[],
  selectedSubRows: MasterListRow[]
): MasterListQuestionOwner[] => {
  const owners: MasterListQuestionOwner[] = selectedCapabilityRows
    .filter((row) => row.subCapabilityCount === 0)
    .map((row) => ({
      ownerKey: `cap-${row.capabilityId}`,
      ownerName: row.capabilityName,
      capabilityName: row.capabilityName,
      capabilityId: row.capabilityId,
      isDirect: true,
    }));

  for (const row of selectedSubRows) {
    owners.push({
      ownerKey: `sub-${row.subId}`,
      ownerName: row.subCapabilityName,
      capabilityName: row.capabilityName,
      capabilityId: row.capabilityId,
      isDirect: false,
    });
  }

  return owners;
};

export type MasterListCoverageIssue = MasterListQuestionOwner & {
  missingCurrent: boolean;
  missingFuture: boolean;
};

export const getQuestionCoverageIssues = (
  owners: MasterListQuestionOwner[],
  selectedQuestionRows: MasterListRow[]
): MasterListCoverageIssue[] => {
  const byOwner = new Map<string, { current: boolean; future: boolean }>();

  for (const row of selectedQuestionRows) {
    const key = getQuestionOwnerKey(row);
    const entry = byOwner.get(key) || { current: false, future: false };
    if (row.questionState === QUESTION_STATE_LABEL[QUESTION_STATE.current]) entry.current = true;
    if (row.questionState === QUESTION_STATE_LABEL[QUESTION_STATE.future]) entry.future = true;
    byOwner.set(key, entry);
  }

  return owners
    .map((owner) => {
      const entry = byOwner.get(owner.ownerKey) || { current: false, future: false };
      return {
        ...owner,
        missingCurrent: !entry.current,
        missingFuture: !entry.future,
      };
    })
    .filter((issue) => issue.missingCurrent || issue.missingFuture);
};

export const formatCoverageMissingParts = (issue: MasterListCoverageIssue) => {
  if (issue.missingCurrent && issue.missingFuture) return 'Current State and Future State';
  if (issue.missingCurrent) return 'Current State';
  return 'Future State';
};

export const formatQuestionCoverageMessage = (issues: MasterListCoverageIssue[]) => {
  if (!issues.length) return '';
  const names = issues.slice(0, 3).map((issue) =>
    issue.isDirect ? `${issue.ownerName} (direct questions)` : issue.ownerName
  );
  const more = issues.length > 3 ? ` and ${issues.length - 3} more` : '';
  return `Each capability and sub-capability needs at least one Current State and one Future State question. Missing for: ${names.join(', ')}${more}.`;
};

/** Capabilities that have children but none of those children were selected. */
export const getCapabilitiesMissingSubSelection = (
  selectedCapabilityRows: MasterListRow[],
  selectedSubRows: MasterListRow[]
) => {
  const covered = new Set(selectedSubRows.map((row) => row.capabilityId));
  return selectedCapabilityRows.filter(
    (row) => row.subCapabilityCount > 0 && !covered.has(row.capabilityId)
  );
};

export const formatMissingSubSelectionMessage = (capabilities: MasterListRow[]) => {
  const names = capabilities.map((row) => row.capabilityName).filter(Boolean);
  if (!names.length) return '';
  const shown = names.slice(0, 3).join(', ');
  const more = names.length > 3 ? ` and ${names.length - 3} more` : '';
  return `Select at least one sub-capability for: ${shown}${more}.`;
};

export type MasterListPickerStatus = {
  complete: boolean;
  label: string;
  tone: 'warn' | 'ready';
};

export const getCapabilityPickerStatus = (
  cap: MasterListRow,
  selectedSubRowsForCap: MasterListRow[]
): MasterListPickerStatus => {
  if (cap.subCapabilityCount > 0) {
    if (selectedSubRowsForCap.length === 0) {
      return { complete: false, label: 'Choose sub-capabilities', tone: 'warn' };
    }

    return {
      complete: true,
      label: `${selectedSubRowsForCap.length} of ${cap.subCapabilityCount} selected`,
      tone: 'ready',
    };
  }

  return {
    complete: true,
    label: cap.questionCount > 0 ? `${cap.questionCount} questions included` : 'No questions yet',
    tone: cap.questionCount > 0 ? 'ready' : 'warn',
  };
};

export type FrameworkDraftKind = 'capability' | 'subCapability' | 'question';

export type FrameworkDraftRow = {
  rowKey: string;
  kind: FrameworkDraftKind;
  name: string;
  typeLabel: string;
  stateLabel: string;
  readiness: MasterListPickerStatus | null;
  capabilityId: number;
  ownerKey: string;
  isDirect: boolean;
  depth: number;
};

/** Flattened tree of the current Traxccel Framework draft, selected items only. */
export const buildFrameworkDraftRows = (
  selectedCapabilityRows: MasterListRow[],
  selectedSubRows: MasterListRow[]
): FrameworkDraftRow[] => {
  const subsByCap = groupRowsByCapabilityId(selectedSubRows);
  const rows: FrameworkDraftRow[] = [];

  for (const cap of selectedCapabilityRows) {
    const selectedSubs = subsByCap.get(cap.capabilityId) || EMPTY_MASTER_LIST_ROWS;
    const isDirect = cap.subCapabilityCount === 0;
    rows.push({
      rowKey: cap.rowKey,
      kind: 'capability',
      name: cap.capabilityName,
      typeLabel: 'Capability',
      stateLabel: isDirect && cap.questionCount > 0 ? `${cap.questionCount} questions` : '',
      readiness: getCapabilityPickerStatus(cap, selectedSubs),
      capabilityId: cap.capabilityId,
      ownerKey: cap.rowKey,
      isDirect,
      depth: 0,
    });

    if (isDirect) continue;

    for (const sub of selectedSubs) {
      rows.push({
        rowKey: sub.rowKey,
        kind: 'subCapability',
        name: sub.subCapabilityName,
        typeLabel: 'Sub-capability',
        stateLabel: sub.questionCount > 0 ? `${sub.questionCount} questions` : '',
        readiness: getSubPickerStatus(sub),
        capabilityId: cap.capabilityId,
        ownerKey: sub.rowKey,
        isDirect: false,
        depth: 1,
      });
    }
  }

  return rows;
};

export const getSubPickerStatus = (sub: MasterListRow): MasterListPickerStatus => {
  if (sub.questionCount === 0) {
    return { complete: true, label: 'No questions yet', tone: 'warn' };
  }

  return {
    complete: true,
    label: `${sub.questionCount} questions included`,
    tone: 'ready',
  };
};

/** Question rows implied by the current capability / sub-capability selection. */
export const resolveQuestionRowsForSelection = (
  selectedCapabilityRows: MasterListRow[],
  selectedSubRows: MasterListRow[],
  allQuestionRows: MasterListRow[]
): MasterListRow[] => {
  const selectedSubIds = new Set(
    selectedSubRows.map((row) => row.subId).filter((id) => id > 0)
  );
  const directCapabilityIds = new Set(
    selectedCapabilityRows
      .filter((row) => row.subCapabilityCount === 0)
      .map((row) => row.capabilityId)
  );

  return allQuestionRows.filter((row) => {
    if (row.subId > 0) return selectedSubIds.has(row.subId);
    return directCapabilityIds.has(row.capabilityId);
  });
};

/** IDs to send when creating a Traxccel Framework from Master List checkboxes. */
export const collectCatalogIdsFromMasterListRows = (rows: MasterListRow[]) => ({
  frameworkIds: uniquePositiveIds(rows.map((row) => row.frameworkId)),
  capabilityIds: uniquePositiveIds(rows.map((row) => row.capabilityId)),
  subCapabilityIds: uniquePositiveIds(rows.map((row) => row.subId)),
  questionIds: uniquePositiveIds(rows.map((row) => row.questionId)),
});

/**
 * Build the create payload from capability/sub selections.
 * Questions are derived automatically; the API also expands them server-side.
 */
export const collectFrameworkSelectionIds = (
  selectedCapabilityRows: MasterListRow[],
  selectedSubRows: MasterListRow[],
  allQuestionRows: MasterListRow[]
) => {
  const impliedQuestions = resolveQuestionRowsForSelection(
    selectedCapabilityRows,
    selectedSubRows,
    allQuestionRows
  );

  return collectCatalogIdsFromMasterListRows([
    ...selectedCapabilityRows,
    ...selectedSubRows,
    ...impliedQuestions,
  ]);
};

export type MasterListQuestionPreview = {
  title: string;
  subtitle: string;
  current: MasterListRow[];
  future: MasterListRow[];
  /** Optional extra context shown beside the questions. */
  details?: {
    description?: string;
    capabilityName?: string;
    userType?: string;
    active?: boolean;
    framework?: any;
    frameworkLabel?: string;
    sourceTypeName?: string;
    maturityOptions?: any[];
    customMaturity?: boolean;
    subCapability?: any;
  };
};

/** Build modal preview content for a capability or sub-capability row. */
export const buildQuestionPreview = (
  owner: MasterListRow,
  allQuestionRows: MasterListRow[]
): MasterListQuestionPreview => {
  const rows =
    owner.viewMode === 'subCapability'
      ? allQuestionRows.filter((row) => row.subId === owner.subId)
      : allQuestionRows.filter(
          (row) => row.capabilityId === owner.capabilityId && !(row.subId > 0)
        );
  const { current, future } = splitQuestionRowsByState(rows);

  const title =
    owner.viewMode === 'subCapability'
      ? owner.subCapabilityName || 'Sub-capability questions'
      : owner.capabilityName || 'Capability questions';
  const subtitle =
    owner.viewMode === 'subCapability'
      ? [owner.capabilityName, owner.sourceFrameworkName].filter(Boolean).join(' · ')
      : [owner.businessTypeName, owner.sourceFrameworkName].filter(Boolean).join(' · ');

  return { title, subtitle, current, future };
};

export async function fetchAllCapabilitiesPages(
  fetchPage: (page: number, pageSize: number) => Promise<{ items: unknown[]; totalCount: number }>,
  pageSize = 500
) {
  let page = 1;
  let all: unknown[] = [];
  let totalCount = 0;

  while (true) {
    const result = await fetchPage(page, pageSize);
    all = all.concat(result.items || []);
    totalCount = result.totalCount || 0;
    if (all.length >= totalCount || !(result.items?.length)) break;
    page += 1;
  }

  return normalizeCapabilitiesList(all);
}
